import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import nwAnalytics from '@nerdwallet/analytics';
import {
  Impression,
  ImpressionContext,
} from '@nerdwallet/analytics/react/impression';
import { Box, Link, Type } from '@nerdwallet/currency';
import CloseIcon from '@nerdwallet/react-icon/nerdwallet-ui/Close';

import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import SegmentImpression from '../../segment-impression';
import NewFeatureTag from './new-feature-tag/new-feature-tag';
import { BOA_2024 } from '../../../lib/paths';
import useAnalytics from '~/client/lib/segment/useAnalytics';
import styles from './announcement-banner.module.less';

interface Props {
  handleOnClose: () => void;
}

const AnnouncementBanner: React.FC<Props> = ({ handleOnClose = () => {} }) => {
  const { trackSegmentTrackEvent } = useAnalytics();
  const [isDesktop, setIsDesktop] = useState<boolean>();
  const [segmentImpressionId] = useState(uuidv4());

  useEffect(() => {
    const userAgent = navigator?.userAgent;

    // No link for mobile
    setIsDesktop(userAgent?.indexOf('Mobi') === -1);
  }, []);

  return (
    <SegmentImpression
      eventName="BOA 2024 Banner Viewed"
      eventProps={{
        impression_id: segmentImpressionId,
        section_name: isDesktop ? 'desktop_homepage' : 'mobile_web_homepage',
      }}
    >
      <Impression
        eventType="element_impression"
        entityName="boa_2024_announcement_banner"
        payload={{
          section_name: isDesktop ? 'desktop_homepage' : 'mobile_web_homepage',
        }}
      >
        <ImpressionContext.Consumer>
          {({ impressionId }) => (
            <Box
              className={styles.bannerContainer}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                flexDirection={{ desktop: 'row', mobile: 'column' }}
                className={styles.bannerContent}
              >
                <NewFeatureTag
                  classNames={classNames([
                    styles.tagMobile,
                    classes('color-white'),
                    classes('background-green-light'),
                  ])}
                />
                <Box
                  display="flex"
                  flexDirection={{
                    desktop: isDesktop ? 'row' : 'column',
                    mobile: 'column',
                  }}
                  marginLeft={{ desktop: 2, mobile: 0 }}
                  marginTop={{ desktop: 0, mobile: 1 }}
                >
                  <Box marginRight={{ desktop: 2, mobile: 0 }}>
                    <Type bold color="green-darkest">
                      The 2024 Best-Of Awards are here — shop our Nerds’ top
                      credit cards, personal loans, and more.
                    </Type>
                  </Box>
                  <Box marginTop={{ desktop: 0, mobile: 1 }}>
                    <Link
                      className={styles.link}
                      color="green-darkest"
                      href={`${BOA_2024}?trk=announcement_banner`}
                      onClick={() => {
                        trackSegmentTrackEvent('BOA 2024 Banner CTA Clicked', {
                          parent_impression_id: segmentImpressionId,
                          section_name: 'desktop_homepage',
                        });
                        nwAnalytics.track('element_interacted', {
                          action: 'select',
                          entity_name:
                            'boa_2024_announcement_banner_cta_clicked',
                          impression_id: impressionId,
                          interaction_type: 'press',
                        });
                      }}
                    >
                      See the list
                    </Link>
                  </Box>
                </Box>
              </Box>
              <Box display={{ desktop: 'flex', mobile: 'none' }}>
                <CloseIcon
                  className={styles.closeIcon}
                  color="#8B8C8F"
                  onClick={() => {
                    trackSegmentTrackEvent(
                      'BOA 2024 Announcement Banner Closed',
                      {
                        parent_impression_id: segmentImpressionId,
                        section_name: 'desktop_homepage',
                      }
                    );
                    nwAnalytics.track('element_interacted', {
                      action: 'close',
                      entity_name: 'boa_2024_announcement_banner_closed',
                      impression_id: impressionId,
                      interaction_type: 'press',
                    });
                    handleOnClose();
                  }}
                  size="medium"
                />
              </Box>
            </Box>
          )}
        </ImpressionContext.Consumer>
      </Impression>
    </SegmentImpression>
  );
};

export default AnnouncementBanner;
